import dayjs from "dayjs";

export const STANDARD = "THRESHOLD_3_6";
export const CALENDAR_DAY = "CALENDAR_DAY";
export const TWENTY_FOUR_HOUR = "CONFIGURABLE_24_HOUR";

export const MODE_ORDER = [STANDARD, CALENDAR_DAY, TWENTY_FOUR_HOUR];

export const MODE_LABELS = {
  [STANDARD]: "Standard (3/6-hour)",
  [CALENDAR_DAY]: "Calendar day",
  [TWENTY_FOUR_HOUR]: "24-hour",
};

// The one-liner under each mode card.
export const MODE_BLURB = {
  [STANDARD]:
    "Each 24 hours from admission is a day. Leftover hours: under 3h free, 3–6h half, 6h+ full.",
  [CALENDAR_DAY]:
    "A new day at midnight. Middle dates are full days; first, last and same-day dates use your rules.",
  [TWENTY_FOUR_HOUR]:
    "Each 24 hours from admission is a day. You set how leftover hours are charged.",
};

export const MODE_ICON = {
  [STANDARD]: "clock",
  [CALENDAR_DAY]: "moon",
  [TWENTY_FOUR_HOUR]: "loop",
};

export const MODE_TABLES = {
  [STANDARD]: [],
  [CALENDAR_DAY]: [
    { key: "first_day", label: "First day", help: "Hours from admission to midnight" },
    { key: "last_day", label: "Last day", help: "Hours from midnight to discharge" },
    { key: "same_day", label: "Same-day stay", help: "Admitted and discharged on the same date" },
  ],
  [TWENTY_FOUR_HOUR]: [
    { key: "remainder", label: "Leftover hours", help: "Hours left after the last full 24h" },
  ],
};

// Appended to the active rule tab's intro, so the part you cannot change is
// stated next to the part you can.
export const MODE_FIXED_NOTE = {
  [CALENDAR_DAY]: "middle dates are always a full day",
  [TWENTY_FOUR_HOUR]: "each full 24h is a full day",
};

export const FRACTIONS = [
  { value: 0, label: "No charge" },
  { value: 0.5, label: "Half day" },
  { value: 1, label: "Full day" },
];

export const FRACTION_SHORT = { 0: "free", 0.5: "½ day", 1: "1 day" };
export const FRACTION_CLASS = { 0: "free", 0.5: "half", 1: "full" };

export const fractionLabel = (value) =>
  FRACTIONS.find((f) => f.value === Number(value))?.label || "-";

export const DEFAULT_TIERS = [
  { up_to_hours: 2, inclusive: true, fraction: 0 },
  { up_to_hours: 6, inclusive: false, fraction: 0.5 },
  { up_to_hours: null, fraction: 1 },
];

// Standard is fixed across Curengo - shown on the ruler, never editable.
export const STANDARD_TIERS = [
  { up_to_hours: 3, inclusive: false, fraction: 0 },
  { up_to_hours: 6, inclusive: false, fraction: 0.5 },
  { up_to_hours: null, fraction: 1 },
];

export const defaultConfiguration = (mode) =>
  Object.fromEntries(
    (MODE_TABLES[mode] || []).map(({ key }) => [key, DEFAULT_TIERS.map((t) => ({ ...t }))])
  );

/* ---- tier shape ---------------------------------------------------------
   A table is a list of bounded steps followed by one open step
   (up_to_hours: null) that catches everything above them. Payloads from the
   API are trusted to be in that order; anything hand-edited is put back into
   it before it reaches the editor. */
export const normalizeTiers = (tiers) => {
  const list = Array.isArray(tiers) && tiers.length ? tiers : DEFAULT_TIERS;
  const bounded = list
    .filter((t) => t?.up_to_hours !== null && t?.up_to_hours !== undefined && t?.up_to_hours !== "")
    .map((t) => ({
      up_to_hours: Number(t.up_to_hours),
      inclusive: !!t.inclusive,
      fraction: Number(t.fraction) || 0,
    }));
  const open = list.find((t) => t?.up_to_hours === null || t?.up_to_hours === undefined);
  return [...bounded, { up_to_hours: null, fraction: open ? Number(open.fraction) || 0 : 1 }];
};

export const normalizeConfiguration = (mode, configuration) =>
  Object.fromEntries(
    (MODE_TABLES[mode] || []).map(({ key }) => [key, normalizeTiers(configuration?.[key])])
  );

export const boundedTiers = (tiers = []) => tiers.filter((t) => t.up_to_hours != null);
export const openFraction = (tiers = []) => {
  const open = tiers.find((t) => t.up_to_hours == null);
  return open ? Number(open.fraction) || 0 : 1;
};

export const tiersFor = (mode, configuration, key) =>
  mode === STANDARD ? STANDARD_TIERS : normalizeTiers(configuration?.[key]);

/* ---- reading a table back ------------------------------------------------ */
export const fh = (hours) => String(Math.round(Number(hours || 0) * 10) / 10).replace(/\.0$/, "");

// Neighbouring steps that charge the same read as one band on the ruler.
export const bandsOf = (tiers = []) => {
  const out = [];
  let lo = 0;
  boundedTiers(tiers).forEach((tier) => {
    const hours = Number(tier.up_to_hours) || 0;
    if (hours <= lo) return;
    const last = out[out.length - 1];
    if (last && last.fraction === Number(tier.fraction)) {
      last.to = hours;
      last.inclusive = !!tier.inclusive;
    } else {
      out.push({ from: lo, to: hours, fraction: Number(tier.fraction), inclusive: !!tier.inclusive });
    }
    lo = hours;
  });
  const above = openFraction(tiers);
  const last = out[out.length - 1];
  if (last && last.fraction === above) {
    last.to = 24;
    last.open = true;
  } else {
    out.push({ from: lo, to: 24, fraction: above, open: true });
  }
  return out;
};

// "Up to 2h: No charge · 2–6h: Half day · From 6h: Full day"
export const describeTiers = (tiers = []) =>
  bandsOf(tiers)
    .map((band) => {
      const where = band.open
        ? band.from
          ? `From ${fh(band.from)}h`
          : "Any stay"
        : band.from
        ? `${fh(band.from)}–${fh(band.to)}h`
        : `${band.inclusive ? "Up to" : "Under"} ${fh(band.to)}h`;
      return `${where}: ${fractionLabel(band.fraction)}`;
    })
    .join(" · ");

const shortOf = (tiers = []) => {
  const first = boundedTiers(tiers)[0];
  if (!first) return `always ${FRACTION_SHORT[openFraction(tiers)]}`;
  const hours = `${fh(first.up_to_hours)}h`;
  return Number(first.fraction) === 0
    ? `free up to ${hours}`
    : `${FRACTION_SHORT[Number(first.fraction)]} up to ${hours}`;
};

// The second line of a facility row - the policy in one glance.
export const summaryOf = (policy) => {
  if (!policy) return "-";
  const mode = policy.billing_mode;
  if (mode === STANDARD) return "3h–6h leftover: half day · 6h or more: full day";
  const config = policy.configuration || {};
  if (mode === TWENTY_FOUR_HOUR) {
    return `Leftover — ${describeTiers(normalizeTiers(config.remainder))}`;
  }
  return `First day ${shortOf(normalizeTiers(config.first_day))} · Last day ${shortOf(
    normalizeTiers(config.last_day)
  )}`;
};

/* ---- editing a table ----------------------------------------------------
   Steps climb: each charges more than the one above it. A choice that would
   not is offered greyed out with the reason, and a change higher up lifts
   anything below it that it has overtaken. */
export const minFractionFor = (tiers, index) => {
  const bounded = boundedTiers(tiers);
  const previous = index === 0 ? null : bounded[index - 1];
  return previous ? Math.min(1, Number(previous.fraction) + 0.5) : 0;
};

export const minFractionForOpen = (tiers) => {
  const bounded = boundedTiers(tiers);
  const last = bounded[bounded.length - 1];
  return last ? Math.min(1, Number(last.fraction) + 0.5) : 0;
};

export const normalizeFractions = (tiers) => {
  const bounded = boundedTiers(tiers);
  let lowest = null;
  const lifted = bounded.map((tier) => {
    const fraction =
      lowest != null && Number(tier.fraction) < lowest + 0.5
        ? Math.min(1, lowest + 0.5)
        : Number(tier.fraction);
    lowest = fraction;
    return { ...tier, fraction };
  });
  let above = openFraction(tiers);
  if (lowest != null && above < lowest + 0.5) above = Math.min(1, lowest + 0.5);
  return [...lifted, { up_to_hours: null, fraction: above }];
};

// Once a step is a full day there is nothing left below it to climb to.
export const canAddTier = (tiers = []) => {
  const bounded = boundedTiers(tiers);
  return !bounded.length || Number(bounded[bounded.length - 1].fraction) < 0.5;
};

export const addTier = (tiers = []) => {
  const bounded = boundedTiers(tiers);
  const last = bounded[bounded.length - 1];
  const lastHours = last ? Number(last.up_to_hours) || 0 : 0;
  const next = {
    up_to_hours: Math.min(23, lastHours + 2),
    inclusive: false,
    fraction: last ? Math.min(1, Number(last.fraction) + 0.5) : 0,
  };
  return normalizeFractions([...bounded, next, { up_to_hours: null, fraction: openFraction(tiers) }]);
};

export const removeTier = (tiers = [], index) =>
  normalizeFractions([
    ...boundedTiers(tiers).filter((_, i) => i !== index),
    { up_to_hours: null, fraction: openFraction(tiers) },
  ]);

export const patchTier = (tiers = [], index, patch) => [
  ...boundedTiers(tiers).map((tier, i) => (i === index ? { ...tier, ...patch } : tier)),
  { up_to_hours: null, fraction: openFraction(tiers) },
];

export const setOpenFraction = (tiers = [], fraction) => [
  ...boundedTiers(tiers),
  { up_to_hours: null, fraction },
];

// Mirrors the backend rules so the admin sees problems before saving.
export const tierErrors = (tiers = []) => {
  const errors = {};
  let previousHours = 0;
  boundedTiers(tiers).forEach((tier, index) => {
    const hours = tier.up_to_hours;
    if (hours === "" || hours == null || Number.isNaN(Number(hours))) {
      errors[index] = "Enter the hours";
    } else if (Number(hours) <= previousHours) {
      errors[index] = index
        ? `Must be more than ${fh(previousHours)}h, the step above`
        : "Must be more than 0h";
    } else if (Number(hours) >= 24) {
      errors[index] = "Must be under 24h";
    } else {
      previousHours = Number(hours);
    }
  });
  return errors;
};

export const countConfigurationErrors = (mode, configuration) =>
  (MODE_TABLES[mode] || []).reduce(
    (total, { key }) => total + Object.keys(tierErrors(configuration?.[key])).length,
    0
  );

export const configurationErrors = (mode, configuration) =>
  countConfigurationErrors(mode, configuration) > 0;

/* ---- policies and their history ---------------------------------------- */
export const statusLabel = (policy) => {
  const status = String(policy?.status || "").toUpperCase();
  if (status === "CANCELLED") return "Cancelled";
  if (status === "SCHEDULED") return "Scheduled";
  if (policy?.is_current) return "In force";
  if (status === "REPLACED") return "Replaced";
  if (status === "SUPERSEDED") return "Superseded";
  if (status === "ACTIVE" || status === "CURRENT") return "In force";
  return "Retired";
};

export const isScheduled = (policy) => statusLabel(policy) === "Scheduled";
export const isPast = (policy) =>
  ["Retired", "Superseded", "Cancelled", "Replaced"].includes(statusLabel(policy));

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

/* The list endpoint is read defensively: each entry carries a facility and
   its versions, whatever nesting they arrive in. A flat serializer that sends
   `facility` as a bare id is read as an id, never as a name. */
const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

/* The list endpoint returns one entry per facility:
     { facility: {id, name, timezone, ...}, current, next, history: [...] }
   `history` leaves out whatever is scheduled, so the version table is the
   union of history, what is in force, and what is queued next. */
export const normalizePolicyGroups = (payload) => {
  const groups = asArray(payload?.data?.data ?? payload?.data ?? payload);
  return groups
    .map((group) => {
      const facility =
        group?.facility && typeof group.facility === "object" ? group.facility : null;
      const id = facility?.id ?? group?.facility_id ?? group?.facility ?? group?.id;
      const current = group?.current || null;
      const scheduled = group?.next || group?.scheduled || null;

      const byId = new Map();
      [...asArray(group?.history ?? group?.versions ?? group?.policies), current, scheduled]
        .filter(Boolean)
        .forEach((version) =>
          byId.set(version.id ?? `v${version.version}`, {
            ...version,
            configuration: version.configuration || {},
          })
        );
      const versions = [...byId.values()].sort(
        (a, b) => (Number(a.version) || 0) - (Number(b.version) || 0)
      );

      return {
        id,
        facility: {
          id,
          name: firstString(facility?.name, group?.facility_name) || `Facility ${id}`,
          city: firstString(facility?.city, facility?.location),
          timezone: firstString(facility?.timezone, group?.timezone) || "Asia/Kolkata",
        },
        versions,
        current: current || versions.find((v) => v.is_current) || null,
        scheduled: scheduled || versions.find(isScheduled) || null,
      };
    })
    .filter((group) => group.id != null);
};

/* ---- labels ------------------------------------------------------------- */
// KIMS-Kondapur and KIMS-Kurnool would both be "KK" - a KIMS branch takes the
// first two letters of its place instead.
export const initialsOf = (name = "") => {
  const branch = name.match(/^KIMS-(\w{2})/);
  if (branch) return branch[1].toUpperCase();
  return name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

const PATH_LABELS = {
  configuration: null,
  billing_mode: "Billing mode",
  effective_from: "Effective from",
  up_to_hours: "hours",
  fraction: "charge",
  stays: "Test stays",
};

// ["configuration", "last_day", 1, "up_to_hours"] -> "Last day, row 2, hours"
export const pathLabel = (path = []) => {
  const tables = Object.fromEntries(
    Object.values(MODE_TABLES)
      .flat()
      .map(({ key, label }) => [key, label])
  );
  return path
    .map((part) => {
      if (typeof part === "number") return `row ${part + 1}`;
      if (tables[part]) return tables[part];
      return part in PATH_LABELS ? PATH_LABELS[part] : part;
    })
    .filter(Boolean)
    .join(", ");
};

export const apiError = (error, fallback) => {
  const data = error?.response?.data;
  const where = pathLabel(data?.err?.path);
  const text = data?.message || data?.detail || fallback;
  return where ? `${where}: ${text}` : text;
};

export const formatDateTime = (iso) => {
  if (!iso) return "Since the start";
  if (dayjs(iso).year() <= 1970) return "Since the start";
  return dayjs(iso).format("DD MMM YYYY, hh:mm A");
};

// Backend times carry the facility's offset; show them as facility-local wall time.
export const facilityTime = (iso) =>
  iso ? dayjs(iso.slice(0, 19)).format("DD MMM, hh:mm A") : "-";

export const RULE_LABELS = {
  first_day: "First day",
  last_day: "Last day",
  same_day: "Same day",
  full_day: "Full day",
  full_period: "Full 24h",
  remainder: "Leftover",
  legacy_full: "Full 24h",
  legacy_remainder: "Leftover",
};

// The rule tag on a breakdown row is coloured by what it charged.
export const RULE_FRACTION_CLASS = (fraction) => FRACTION_CLASS[Number(fraction)] || "free";

export const formatAmount = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
